import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Phone, MessageCircle, ArrowRight } from "lucide-react";
import { useLanguage } from "@/lib/LanguageContext";
import SEOHead from "@/components/SEOHead";
import api, { formatPrice } from "@/lib/api";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_70076dd7-e4cb-484b-b629-fe23ba9127e6/artifacts/3zpuxtd5_LOGO.PNG";
const HERO_IMG = "https://customer-assets.emergentagent.com/job_70076dd7-e4cb-484b-b629-fe23ba9127e6/artifacts/ysejz4s4_MAIN%20PAGE%20PHOTO.PNG";
const HERO_IMG_MOBILE = "https://customer-assets.emergentagent.com/job_801ea6f2-12bf-4166-ad41-c421f2789649/artifacts/wxnk03dt_3f012d5c_img_2K.jpg";

export default function HomePage() {
  const [houses, setHouses] = useState([]);
  const [services, setServices] = useState([]);
  const [searchParams] = useSearchParams();
  const { t, l, lang } = useLanguage();

  const getHouseTypeLabel = (type) => {
    const key = `houses.${type}`;
    return t(key);
  };

  useEffect(() => {
    // Track UTM params
    const utm = { source: searchParams.get("utm_source"), medium: searchParams.get("utm_medium"), campaign: searchParams.get("utm_campaign") };
    if (utm.source) sessionStorage.setItem("utm_data", JSON.stringify(utm));

    api.get("/houses").then((r) => setHouses(r.data.slice(0, 6))).catch(() => {});
    api.get("/services?category=spa_program").then((r) => setServices(r.data.slice(0, 4))).catch(() => {});
  }, [searchParams]);

  return (
    <div>
      <SEOHead path="/" />
      {/* Hero */}
      <section className="hero-section relative h-[100svh] md:h-screen flex items-start pt-20 md:items-center md:pt-0" data-testid="hero-section">
        <img src={HERO_IMG_MOBILE} alt="Eco Village KZO" className="hero-img-mobile absolute inset-0 w-full h-full object-cover md:hidden" loading="eager" />
        <img src={HERO_IMG} alt="Eco Village KZO" className="hero-img absolute inset-0 w-full h-full object-cover hidden md:block" loading="eager" />
        <div className="hero-overlay absolute inset-0" />
        <div className="relative z-10 max-w-[1400px] mx-auto px-4 md:px-8 w-full">
          <div className="max-w-2xl">
            <p className="hero-text-stroke-sm text-sm uppercase tracking-[0.3em] text-[#5EC75E] font-sans mb-4 opacity-0 animate-fade-in" style={{ animationDelay: "0.2s" }}>
              {t("home.subtitle")}
            </p>
            <h1 className="hero-text-stroke text-[2rem] sm:text-[2.5rem] md:text-7xl font-serif tracking-tight leading-[1.1] mb-6 md:mb-8 opacity-0 animate-fade-in text-white" style={{ animationDelay: "0.4s" }}>
              {t("home.title1")} <br /><span className="text-gold-gradient italic">{t("home.title2")}</span> {t("home.title3")}
            </h1>
            <div className="flex flex-wrap gap-4 opacity-0 animate-fade-in" style={{ animationDelay: "0.6s" }}>
              <Link to="/houses" data-testid="hero-houses-btn">
                <Button variant="outline" className="border-white/40 text-white hover:bg-white/10 px-8 py-6 rounded-none uppercase tracking-widest text-sm">
                  {t("nav.houses")}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Houses Preview */}
      <section className="py-24 md:py-32 bg-card" data-testid="houses-preview-section">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="flex items-end justify-between mb-12">
            <div>
              <p className="text-sm uppercase tracking-[0.3em] text-primary font-sans mb-2">{t("nav.houses")}</p>
              <h2 className="text-4xl md:text-5xl font-serif tracking-tight">{t("home.ourHouses")}</h2>
            </div>
            <Link to="/houses" className="hidden md:flex items-center gap-2 text-primary text-sm hover:underline" data-testid="view-all-houses">
              {t("home.allHouses")} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {houses.map((h) => (
              <Link to={`/houses/${h.id}`} key={h.id} className="group relative overflow-hidden bg-background border border-border hover:border-primary/20 transition-all duration-500 card-glow" data-testid={`house-card-${h.id}`}>
                <div className="aspect-[4/3] overflow-hidden">
                  <img src={h.photos?.[0] || ""} alt={h.name} className="w-full h-full object-cover img-hover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs uppercase tracking-widest text-primary font-sans">{getHouseTypeLabel(h.house_type)}</span>
                    <span className="text-xs text-muted-foreground">{h.capacity_max} {t("common.persons")} {t("common.upTo")}</span>
                  </div>
                  <h3 className="font-serif text-xl mb-2">{l(h)}</h3>
                  <p className="text-primary font-sans font-semibold">{formatPrice(h.price_per_hour)} {t("common.perHour")}</p>
                </div>
              </Link>
            ))}
          </div>
          <Link to="/houses" className="md:hidden flex items-center justify-center gap-2 text-primary text-sm mt-6 hover:underline">
            {t("home.allHouses")} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* Popular Services */}
      {services.length > 0 && (
        <section className="py-24 md:py-32" data-testid="services-preview-section">
          <div className="max-w-[1400px] mx-auto px-4 md:px-8">
            <p className="text-sm uppercase tracking-[0.3em] text-primary font-sans mb-2">{t("nav.services")}</p>
            <h2 className="text-4xl md:text-5xl font-serif tracking-tight mb-12">{t("home.spaPrograms")}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {services.map((s) => (
                <div key={s.id} className="flex gap-6 p-6 bg-card border border-border hover:border-primary/20 transition-all" data-testid={`service-preview-${s.id}`}>
                  <div className="flex-1">
                    <h3 className="font-serif text-xl mb-2">{l(s)}</h3>
                    <p className="text-sm text-muted-foreground mb-3">{l(s, "description")}</p>
                    <div className="flex items-center gap-4">
                      <span className="text-primary font-semibold">{formatPrice(s.price)}</span>
                      {s.duration && <span className="text-xs text-muted-foreground">{s.duration}</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <Link to="/services" className="inline-flex items-center gap-2 text-primary text-sm mt-8 hover:underline" data-testid="view-all-services">
              {t("home.allServices")} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      )}

      {/* Contact Block */}
      <section className="py-24 md:py-32 bg-card" data-testid="contact-block">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-serif tracking-tight mb-6">{t("home.contactUs")}</h2>
          <p className="text-muted-foreground mb-8 max-w-lg mx-auto">{t("home.contactDesc")}</p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="tel:+77027033030" data-testid="contact-phone-btn">
              <Button variant="outline" className="border-primary/20 text-primary hover:bg-primary/10 px-6 py-5 rounded-none gap-2">
                <Phone className="w-4 h-4" /> +7 702 703 30 30
              </Button>
            </a>
            <a href="https://wa.me/77027033030" target="_blank" rel="noreferrer" data-testid="contact-whatsapp-btn">
              <Button className="bg-[#5EC75E] hover:bg-[#4CAF50] text-white px-6 py-5 rounded-none gap-2">
                <MessageCircle className="w-4 h-4" /> WhatsApp
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
